<template>
  <div class="flex flex-col h-screen supports-[height:100dvh]:h-[100dvh]">
    <!-- Header -->
    <div class="flex-shrink-0 p-3 md:p-4 pb-2">
      <div class="flex items-center gap-2 md:gap-3 mb-3 md:mb-4">
        <el-input
          v-model="search"
          :placeholder="t('searchTask')"
          clearable
          class="flex-1 min-w-0"
          @input="onSearchInput"
          @clear="fetchTasks"
        >
          <template #prefix>
            <Icon icon="mdi:magnify" class="w-4 h-4 text-gray-400" />
          </template>
        </el-input>

        <button
          class="flex items-center gap-1 px-3 py-2 rounded-lg border text-sm transition-colors bg-[#E2E8F0] flex-shrink-0"
          :class="myTasksOnly ? 'border-blue-300 text-blue-700' : 'border-gray-200 text-gray-600 hover:bg-gray-100'"
          :title="t('myTasks')"
          :aria-pressed="myTasksOnly"
          @click="toggleMyTasks"
        >
          <Icon icon="fa-solid:tasks" class="w-4 h-4" />
          <Icon icon="mdi:account-outline" class="w-4 h-4" />
        </button>

        <button
          v-if="$can('create', '/hr/tasks')"
          class="flex items-center gap-2 bg-gray-900 text-white px-3 md:px-4 py-2 rounded-lg text-sm font-medium hover:bg-gray-700 transition-colors whitespace-nowrap flex-shrink-0"
          :title="t('newTask')"
          @click="openCreateDialog"
        >
          <Icon icon="mdi:plus" class="w-4 h-4 md:hidden" />
          <span class="hidden md:inline">{{ t('newTask') }}</span>
        </button>
      </div>

      <!-- Mobile status tabs -->
      <div v-if="isMobile" class="grid grid-cols-4 gap-1 p-1 bg-gray-100 rounded-xl" role="tablist">
        <button
          v-for="col in columns"
          :key="col.status"
          type="button"
          role="tab"
          :aria-selected="activeStatus === col.status"
          class="flex flex-col items-center justify-center gap-0.5 py-1.5 px-1 rounded-lg text-[11px] font-medium transition-colors min-w-0"
          :class="activeStatus === col.status ? 'bg-white shadow-sm text-gray-900' : 'text-gray-500'"
          @click="activeStatus = col.status"
        >
          <span class="flex items-center gap-1">
            <Icon :icon="col.icon" class="w-4 h-4" />
            <span class="text-xs font-semibold">{{ columnCount[col.status] }}</span>
          </span>
          <span class="truncate max-w-full">{{ t(col.labelKey) }}</span>
        </button>
      </div>
    </div>

    <!-- Kanban Board -->
    <div class="flex-1 flex gap-3 lg:gap-4 px-3 md:px-4 pb-3 md:pb-4 min-h-0 overflow-x-auto overflow-y-hidden md:snap-x">
      <div
        v-for="col in visibleColumns"
        :key="col.status"
        class="flex-1 bg-white rounded-xl border border-gray-200 flex flex-col min-w-0 md:min-w-[260px] overflow-hidden md:snap-start"
      >
        <!-- Column header -->
        <div class="flex-shrink-0 p-3 border-b border-gray-100 rounded-t-xl" :class="col.headerBg">
          <div class="flex items-center gap-2">
            <Icon :icon="col.icon" class="w-5 h-5 flex-shrink-0" :class="col.iconClass" />
            <span class="font-semibold text-sm text-gray-800 truncate">{{ t(col.labelKey) }}</span>
            <span class="text-xs text-gray-400 bg-gray-100 rounded-full px-1.5 py-0.5 flex-shrink-0">
              {{ columnCount[col.status] }}
            </span>
            <div class="ms-auto flex items-center gap-1 flex-shrink-0">
              <button
                class="p-1 md:p-0.5 transition-colors"
                :class="showDeletedColumns[col.status] ? 'text-red-500' : 'text-gray-400 hover:text-red-500'"
                :title="t('showDeleted')"
                @click="toggleShowDeleted(col.status)"
              >
                <Icon icon="mdi:trash-can-outline" class="w-4 h-4" />
              </button>
              <el-dropdown @command="(cmd: string) => setSortForColumn(col.status, cmd)" trigger="click">
                <button class="text-gray-400 hover:text-gray-600 p-1 md:p-0.5" :title="t('sort')">
                  <Icon icon="mdi:sort-alphabetical-ascending" class="w-4 h-4" />
                </button>
                <template #dropdown>
                  <el-dropdown-menu>
                    <el-dropdown-item :command="col.status + ':newest'">{{ t('newerFirst') }}</el-dropdown-item>
                    <el-dropdown-item :command="col.status + ':oldest'">{{ t('olderFirst') }}</el-dropdown-item>
                    <el-dropdown-item :command="col.status + ':dueDate'">{{ t('byDueDate') }}</el-dropdown-item>
                  </el-dropdown-menu>
                </template>
              </el-dropdown>
              <button
                class="text-xs px-1.5 py-0.5 rounded transition-colors flex-shrink-0"
                :class="dueOnlyColumns[col.status] ? 'text-red-600' : 'text-gray-400 hover:text-gray-600'"
                @click="toggleDueOnly(col.status)"
              >
                {{ t('dueOnly') }}
              </button>
            </div>
          </div>
        </div>

        <!-- Draggable task list -->
        <div class="flex-1 overflow-y-auto p-2 relative">
          <draggable
            v-model="columnTasks[col.status]"
            group="tasks"
            item-key="id"
            class="min-h-full"
            :delay="200"
            :delay-on-touch-only="true"
            :touch-start-threshold="5"
            ghost-class="task-ghost"
            @change="(evt: any) => onColumnChange(evt, col.status)"
          >
            <template #item="{ element }">
              <div
                class="group"
                :class="{ hidden: dueOnlyColumns[col.status] && !hasDueDate(element) }"
              >
                <TaskCard
                  :task="element"
                  :statuses="isMobile ? columns : []"
                  @click="openEditDialog(element)"
                  @delete="deleteTask(element)"
                  @move="(status: string) => moveTask(element, status)"
                />
              </div>
            </template>
          </draggable>
          <div
            v-if="visibleCount(col.status) === 0"
            class="absolute inset-0 flex flex-col items-center justify-center text-center text-gray-400 text-sm pointer-events-none"
          >
            <Icon :icon="col.icon" class="w-8 h-8 mb-2 opacity-50" />
            {{ t('noTasks') }}
          </div>
        </div>
      </div>
    </div>

    <TaskDialog
      v-model="dialogVisible"
      :mode="dialogMode"
      :initial-task="selectedTask"
      :users="users"
      @saved="onTaskSaved"
      @commented="onTaskCommented"
      @status-changed="onTaskStatusChanged"
      @attachments-changed="onTaskAttachmentsChanged"
    />
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, computed, onMounted } from 'vue'
import { useMediaQuery } from '@vueuse/core'
import { useI18n } from 'vue-i18n'
import { useRoute } from 'vue-router'
import { Icon } from '@iconify/vue'
import draggable from 'vuedraggable'
import { useHTTP } from '@/plugins/http'
import Task from '@/models/hr/Task'
import User from '@/models/user/User'
import TaskCard from './TaskCard.vue'
import TaskDialog from './TaskDialog.vue'
import { ElMessage, ElMessageBox } from 'element-plus'

const STATUSES = ['todo', 'in_progress', 'done', 'canceled'] as const
type Status = typeof STATUSES[number]

export default defineComponent({
  name: 'Tasks',
  components: { Icon, draggable, TaskCard, TaskDialog },
  setup() {
    const { t } = useI18n({
      messages: {
        en: {
          searchTask: 'Search for task', newTask: 'New Task',
          toDo: 'To Do', inProgress: 'In Progress', done: 'Done', canceled: 'Canceled',
          dueOnly: 'Due only', newerFirst: 'newer first', olderFirst: 'older first',
          byDueDate: 'by due date',
          confirmDelete: 'Are you sure you want to delete this task?',
          confirmPermanentDelete: 'Do you want to delete this task permanently?',
          noTasks: 'No tasks here', myTasks: 'My tasks only', showDeleted: 'Show deleted',
          sort: 'Sort', attachmentUploadFailed: 'Task saved, but "{name}" failed to upload',
        },
        ar: {
          searchTask: 'ابحث عن مهمة', newTask: 'مهمة جديدة',
          toDo: 'للتنفيذ', inProgress: 'قيد التنفيذ', done: 'منفَّذة', canceled: 'ملغيّة',
          dueOnly: 'المتجاوزة للموعد', newerFirst: 'الأحدث أولاً', olderFirst: 'الأقدم أولاً',
          byDueDate: 'حسب الموعد النهائي',
          confirmDelete: 'هل أنت متأكد من حذف هذه المهمة؟',
          confirmPermanentDelete: 'هل تريد حذف هذه المهمة نهائياً؟',
          noTasks: 'لا توجد مهام هنا', myTasks: 'مهامي فقط', showDeleted: 'عرض المحذوفة',
          sort: 'ترتيب', attachmentUploadFailed: 'تم حفظ المهمة، لكن فشل رفع "{name}"',
        }
      }
    })

    const route = useRoute()
    const http = useHTTP()
    const isMobile = useMediaQuery('(max-width: 767px)')
    const activeStatus = ref<Status>('todo')

    const columnTasks = ref<Record<Status, Task[]>>({
      todo: [], in_progress: [], done: [], canceled: []
    })

    const users = ref<User[]>([])
    const search = ref('')
    const myTasksOnly = ref(route.query.my_tasks === 'true')
    const dialogVisible = ref(false)
    const dialogMode = ref<'create' | 'view' | 'edit'>('create')
    const selectedTask = ref<Task | null>(null)
    const columnSorts = ref<Record<Status, string>>({
      todo: 'newest', in_progress: 'newest', done: 'newest', canceled: 'newest'
    })
    const dueOnlyColumns = ref<Record<Status, boolean>>({
      todo: false, in_progress: false, done: false, canceled: false
    })
    const showDeletedColumns = ref<Record<Status, boolean>>({
      todo: false, in_progress: false, done: false, canceled: false
    })

    let searchDebounceTimer: ReturnType<typeof setTimeout> | null = null

    const columns = [
      { status: 'todo' as Status, labelKey: 'toDo', icon: 'mdi:format-list-bulleted', iconClass: 'text-gray-600', headerBg: 'bg-gray-50' },
      { status: 'in_progress' as Status, labelKey: 'inProgress', icon: 'mdi:circle-half-full', iconClass: 'text-gray-600', headerBg: 'bg-yellow-50' },
      { status: 'done' as Status, labelKey: 'done', icon: 'mdi:checkbox-marked-outline', iconClass: 'text-gray-600', headerBg: 'bg-green-50' },
      { status: 'canceled' as Status, labelKey: 'canceled', icon: 'mdi:close-circle-outline', iconClass: 'text-gray-600', headerBg: 'bg-red-50' }
    ]

    const visibleColumns = computed(() =>
      isMobile.value ? columns.filter(c => c.status === activeStatus.value) : columns
    )

    // Returns true when task is overdue (due date is in the past)
    const hasDueDate = (task: Task) => {
      if (!task.dueDate) return false
      const due = new Date(task.dueDate as unknown as string)
      if (due.getFullYear() <= 1970) return false
      return due < new Date()
    }

    // Count badge: visible tasks per column (respects dueOnly filter)
    const columnCount = computed(() => {
      const result = {} as Record<Status, number>
      for (const status of STATUSES) {
        const tasks = columnTasks.value[status]
        result[status] = dueOnlyColumns.value[status]
          ? tasks.filter(hasDueDate).length
          : tasks.length
      }
      return result
    })

    const visibleCount = (status: Status) => columnCount.value[status]

    const applySortToColumn = (status: Status) => {
      const sort = columnSorts.value[status]
      const list = columnTasks.value[status]
      if (sort === 'oldest') {
        list.sort((a, b) => new Date(a.createdAt || '').getTime() - new Date(b.createdAt || '').getTime())
      } else if (sort === 'dueDate') {
        list.sort((a, b) => {
          if (!hasDueDate(a)) return 1
          if (!hasDueDate(b)) return -1
          return new Date(a.dueDate as unknown as string).getTime() - new Date(b.dueDate as unknown as string).getTime()
        })
      } else {
        list.sort((a, b) => new Date(b.createdAt || '').getTime() - new Date(a.createdAt || '').getTime())
      }
    }

    const distributeTasksToColumns = (tasks: Task[]) => {
      const buckets: Record<Status, Task[]> = { todo: [], in_progress: [], done: [], canceled: [] }
      for (const task of tasks) {
        const s = task.status as Status
        if (!buckets[s]) continue
        const isDeleted = !!(task as any).deletedAt
        // Show deleted tasks only in columns with showDeleted on, active tasks only when off
        if (showDeletedColumns.value[s] ? isDeleted : !isDeleted) {
          buckets[s].push(task)
        }
      }
      columnTasks.value = buckets
      for (const status of STATUSES) applySortToColumn(status)
    }

    const anyShowDeleted = computed(() => Object.values(showDeletedColumns.value).some(Boolean))

    const fetchTasks = async () => {
      try {
        const params = new URLSearchParams()
        if (search.value) params.set('search', search.value)
        if (myTasksOnly.value) params.set('my_tasks', 'true')
        if (anyShowDeleted.value) params.set('show_deleted', 'true')
        const data = await http.get<Task[]>(`/hr/tasks?${params.toString()}`)
        distributeTasksToColumns(data || [])
      } catch (e) {
        console.error(e)
      }
    }

    const onSearchInput = () => {
      if (searchDebounceTimer) clearTimeout(searchDebounceTimer)
      searchDebounceTimer = setTimeout(fetchTasks, 400)
    }

    const fetchUsers = async () => {
      try {
        const data = await http.get<User[]>('/user/users')
        users.value = data || []
      } catch (e) {
        console.error(e)
      }
    }

    const updateColumnOrder = async (status: Status) => {
      const items = columnTasks.value[status].map((t, idx) => ({ id: t.id, sortOrder: idx }))
      try {
        await http.put('/hr/tasks/order', items)
      } catch (e) {
        console.error(e)
      }
    }

    const setSortForColumn = async (status: Status, cmd: string) => {
      columnSorts.value[status] = cmd.split(':')[1]
      applySortToColumn(status)
      await updateColumnOrder(status)
    }

    const toggleDueOnly = (status: Status) => {
      dueOnlyColumns.value[status] = !dueOnlyColumns.value[status]
    }

    const toggleShowDeleted = (status: Status) => {
      showDeletedColumns.value[status] = !showDeletedColumns.value[status]
      fetchTasks()
    }

    const toggleMyTasks = () => {
      myTasksOnly.value = !myTasksOnly.value
      fetchTasks()
    }

    const onColumnChange = async (evt: any, toStatus: Status) => {
      if (evt.moved) {
        await updateColumnOrder(toStatus)
        return
      }
      if (!evt.added) return
      const item = evt.added.element as Task
      if (!item) return
      item.status = toStatus
      try {
        await http.put<Task>(`/hr/tasks/${item.id}/status`, { status: toStatus })
        await updateColumnOrder(toStatus)
      } catch (e) {
        console.error(e)
        fetchTasks()
      }
    }

    const moveTask = async (task: Task, status: string) => {
      const toStatus = status as Status
      const fromStatus = task.status as Status
      if (fromStatus === toStatus) return
      columnTasks.value[fromStatus] = columnTasks.value[fromStatus].filter(t => t.id !== task.id)
      task.status = toStatus
      columnTasks.value[toStatus].unshift(task)
      try {
        await http.put<Task>(`/hr/tasks/${task.id}/status`, { status: toStatus })
        await updateColumnOrder(toStatus)
      } catch (e) {
        console.error(e)
        fetchTasks()
      }
    }

    const openCreateDialog = () => {
      selectedTask.value = null
      dialogMode.value = 'create'
      dialogVisible.value = true
    }

    const openEditDialog = (task: Task) => {
      selectedTask.value = { ...task } as Task
      dialogMode.value = 'view'
      dialogVisible.value = true
    }

    const deleteTask = async (task: Task) => {
      const isDeleted = !!(task as any).deletedAt
      try {
        const message = isDeleted ? t('confirmPermanentDelete') : t('confirmDelete')
        await ElMessageBox.confirm(message, '', { type: 'warning' })
        if (isDeleted) {
          await http.delete('/hr/tasks/permanent', { data: [{ id: task.id }] })
        } else {
          await http.delete('/hr/tasks', { data: [{ id: task.id }] })
        }
        const s = task.status as Status
        columnTasks.value[s] = columnTasks.value[s].filter(t => t.id !== task.id)
      } catch (e) {
        // cancelled or error
      }
    }

    const uploadPendingAttachments = async (task: Task, files: File[]) => {
      for (const file of files) {
        try {
          const data = new FormData()
          data.set('attachment', file)
          await http.put(`/hr/tasks/${task.id}/attachments`, data)
          task.attachmentsCount = (task.attachmentsCount || 0) + 1
        } catch (e: any) {
          ElMessage.error(e?.body || t('attachmentUploadFailed', { name: file.name }))
        }
      }
    }

    const onTaskSaved = async (formData: any, pendingFiles: File[] = []) => {
      try {
        if (formData.id) {
          const updated = await http.put<Task>(`/hr/tasks/${formData.id}`, formData)
          if (updated) {
            for (const s of STATUSES) {
              columnTasks.value[s] = columnTasks.value[s].filter(t => t.id !== formData.id)
            }
            const targetStatus = (updated.status || 'todo') as Status
            columnTasks.value[targetStatus].unshift(updated)
          }
        } else {
          const created = await http.post<Task>('/hr/tasks', formData)
          if (created) {
            const s = (created.status || 'todo') as Status
            columnTasks.value[s].unshift(created)
            if (pendingFiles.length) {
              await uploadPendingAttachments(columnTasks.value[s][0], pendingFiles)
            }
          }
        }
      } catch (e) {
        console.error(e)
      }
    }

    const findTask = (taskId: string) => {
      for (const s of STATUSES) {
        const task = columnTasks.value[s].find(t => t.id === taskId)
        if (task) return task
      }
      return null
    }

    const onTaskCommented = (taskId: string, delta = 1) => {
      const task = findTask(taskId)
      if (task) task.commentsCount = Math.max(0, (task.commentsCount || 0) + delta)
    }

    const onTaskAttachmentsChanged = ({ taskId, count }: { taskId: string; count: number }) => {
      const task = findTask(taskId)
      if (task) task.attachmentsCount = count
    }

    const onTaskStatusChanged = () => {
      fetchTasks()
    }

    const openTaskFromQuery = async () => {
      const taskId = route.query.open_task as string
      if (!taskId) return
      try {
        const task = await http.get<Task>(`/hr/tasks/${taskId}`)
        if (task) {
          if (isMobile.value) activeStatus.value = (task.status || 'todo') as Status
          openEditDialog(task)
        }
      } catch (e) {
        console.error(e)
      }
    }

    onMounted(async () => {
      await Promise.all([fetchTasks(), fetchUsers()])
      openTaskFromQuery()
    })

    return {
      t, columnTasks, columnCount, users, search, myTasksOnly, fetchTasks,
      dialogVisible, dialogMode, selectedTask, columns,
      dueOnlyColumns, showDeletedColumns, columnSorts, hasDueDate,
      setSortForColumn, toggleDueOnly, toggleShowDeleted, toggleMyTasks, onSearchInput,
      onColumnChange, openCreateDialog, openEditDialog,
      deleteTask, onTaskSaved, onTaskCommented, onTaskStatusChanged, onTaskAttachmentsChanged,
      isMobile, activeStatus, visibleColumns, visibleCount, moveTask
    }
  }
})
</script>

<style scoped>
:deep(.task-ghost) {
  opacity: 0.4;
}
</style>
